import { useState, useEffect } from "react";
import { Briefcase, Users, Monitor, FileText, Loader2, RefreshCw, Bot, ChevronRight, DollarSign, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import axios from "axios";

const FALLBACK_DASHBOARD = {
  active_contractors: 5, pending_submissions: 12, active_employees: 8, active_onboarding: 2,
  open_offboarding: 1, total_devices: 42, assigned_devices: 36, open_requests: 3,
  payroll_active_runs: 2, payroll_approved_runs: 1, eor_active_assignments: 6, eor_overdue_compliance: 2,
};

export default function SmartPayHub() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/smartpay/dashboard");
      setDashboard(Object.keys(res.data).length > 0 ? res.data : FALLBACK_DASHBOARD);
    } catch (err) {
      setDashboard(FALLBACK_DASHBOARD);
      toast({ title: "Failed to load SmartPay data. Showing demo data.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div><h1 className="text-2xl font-bold">SmartPay Dashboard</h1><p className="text-muted-foreground">Resource Management overview</p></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const kpiCards = [
    { title: "Active Contractors", value: dashboard?.active_contractors || 0, icon: Briefcase, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    { title: "Pending Submissions", value: dashboard?.pending_submissions || 0, icon: FileText, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
    { title: "Active Employees", value: dashboard?.active_employees || 0, icon: Users, color: "text-green-600", bg: "bg-green-50", border: "border-green-200" },
    { title: "Current Onboarding", value: dashboard?.active_onboarding || 0, icon: Users, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
    { title: "Open Offboarding", value: dashboard?.open_offboarding || 0, icon: Users, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
    { title: "Total Devices", value: dashboard?.total_devices || 0, icon: Monitor, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
    { title: "Assigned Devices", value: dashboard?.assigned_devices || 0, icon: Monitor, color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-200" },
    { title: "Open Requests", value: dashboard?.open_requests || 0, icon: FileText, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
    { title: "Payroll Runs", value: dashboard?.payroll_active_runs || 0, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    { title: "Approved for Payment", value: dashboard?.payroll_approved_runs || 0, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    { title: "Active EOR Assignments", value: dashboard?.eor_active_assignments || 0, icon: Globe, color: "text-cyan-600", bg: "bg-cyan-50", border: "border-cyan-200" },
    { title: "Compliance Due", value: dashboard?.eor_overdue_compliance || 0, icon: FileText, color: "text-cyan-600", bg: "bg-cyan-50", border: "border-cyan-200" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">SmartPay Dashboard</h1>
          <p className="text-muted-foreground">Resource Management overview</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(kpi => (
          <Card key={kpi.title} className={`${kpi.bg} ${kpi.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.title}</p>
                  <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button variant="outline" className="h-20" onClick={() => navigate("/smartpay/contractors")}>
              <Briefcase className="h-5 w-5 mr-2" /> New Contract
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/smartpay/hris")}>
              <Users className="h-5 w-5 mr-2" /> Clock In
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/smartpay/equipment")}>
              <Monitor className="h-5 w-5 mr-2" /> Request Device
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/smartpay/payroll")}>
              <DollarSign className="h-5 w-5 mr-2" /> New Payroll
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/smartpay/eor")}>
              <Globe className="h-5 w-5 mr-2" /> EOR Dashboard
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/smartpay/assistant")}>
              <Bot className="h-5 w-5 mr-2" /> AI Assistant
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
